import * as React from 'react';
import { uiMarkerInternal } from '../recipes/shared';
import {
  AlertTriangle,
  CheckCircle2,
  Info,
  XCircle,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@relay-towing/tokens';
import {
  alertBodyRecipe,
  alertIconRecipe,
  alertRecipe,
  alertTitleRecipe,
  type AlertRecipeProps,
} from '../recipes/feedback.recipe';

type Tone = NonNullable<AlertRecipeProps['tone']>;

/** Each tone's default icon, so callers do not have to pick one every time. */
const TONE_ICON: Record<Tone, LucideIcon | null> = {
  neutral: null,
  info: Info,
  success: CheckCircle2,
  warning: AlertTriangle,
  destructive: XCircle,
};

export interface AlertProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'>,
    AlertRecipeProps {
  title?: React.ReactNode;
  /** Replace the tone's default icon, or pass null for none. */
  icon?: LucideIcon | null;
  /** Buttons or a link, aligned to the right. */
  actions?: React.ReactNode;
}

/**
 * A message attached to the page.
 *
 * Use this for a condition that persists — "this impound has no release form
 * on file" — and a toast for something transient like "saved". A toast that
 * disappears is the wrong home for information the user still needs.
 *
 * Destructive alerts announce themselves via role="alert"; the quieter tones
 * do not, so a page full of informational notices does not shout on load.
 */
export const Alert = React.forwardRef<HTMLDivElement, AlertProps>(
  function Alert(
    { className, tone = 'neutral', title, icon, actions, children, ...props },
    ref
  ) {
    // cva's VariantProps admits null as well as undefined, so the default
    // parameter alone does not narrow it to a valid key.
    const resolvedTone: Tone = tone ?? 'neutral';
    const Icon = icon === undefined ? TONE_ICON[resolvedTone] : icon;

    return (
      <div
        ref={ref}
        role={resolvedTone === 'destructive' ? 'alert' : undefined}
        className={cn(alertRecipe({ tone: resolvedTone }), className)}
        {...props}
      >
        {Icon ? (
          <Icon className={alertIconRecipe()} aria-hidden="true" />
        ) : null}
        <div className="relay-ui-internal min-w-0 flex-1 space-y-1">
          {title ? <p className={alertTitleRecipe()}>{title}</p> : null}
          {children ? (
            <div className={alertBodyRecipe()}>{children}</div>
          ) : null}
        </div>
        {actions ? (
          <div className="relay-ui-internal flex shrink-0 items-center gap-2">
            {actions}
          </div>
        ) : null}
      </div>
    );
  }
);

/**
 * COMPOSED PARTS
 *
 * `<Alert title="…">body</Alert>` is the shorter form and the one to prefer.
 * These exist because ~9 alerts in the app were written the composed way:
 *
 *   <Alert tone="warning">
 *     <AlertTitle>Connection lost</AlertTitle>
 *     <AlertDescription>Retrying in 30 seconds.</AlertDescription>
 *   </Alert>
 */
export const AlertTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(function AlertTitle({ className, ...props }, ref) {
  return (
    <p ref={ref} className={cn(alertTitleRecipe(), className)} {...props} />
  );
});

export const AlertDescription = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(function AlertDescription({ className, ...props }, ref) {
  // The body wrapper Alert renders already carries the body type role, so this
  // only needs to be a block — styling it again would double the leading.
  return (
    <div ref={ref} className={cn(uiMarkerInternal, className)} {...props} />
  );
});
